'use client';

import { notifications } from '@mantine/notifications';
import { useCallback, useState, useTransition } from 'react';

import { AppError } from '@/utils/error';
import rollbar from '@/utils/rollbar';

export type TransitionOptions = {
  skipErrorNotification?: boolean;
  successTitle?: string;
  successMessage?: string;
  errorMessage?: string;
};

export function useSafeTransition(
  name: string,
  { skipErrorNotification, successMessage, successTitle, errorMessage }: TransitionOptions = {},
) {
  const [error, setError] = useState<AppError | null>(null);
  const [pending, startTransition] = useTransition();

  const startSafeTransition = useCallback(
    (callback: React.TransitionFunction) => {
      setError(null);
      startTransition(async () => {
        try {
          await callback();

          if (successMessage) {
            notifications.show({
              title: successTitle || 'Operação realizada com sucesso',
              message: successMessage,
              color: 'green',
              autoClose: 6000,
            });
          }
        } catch (error) {
          // Log the original error (only if shouldLog is true)
          if (error instanceof Error && error.name === 'APP_ERROR') {
            const appError = AppError.fromSerializedError(error);

            setError(appError);

            if (appError.shouldLog()) {
              rollbar.error(`Error during "${name}" action:`, appError);
            }

            // Show notification only if not skipped and no specific error handling
            if (!skipErrorNotification) {
              const message = errorMessage || appError.getUserMessage();
              notifications.show({
                title: 'Ocorreu um erro',
                message,
                color: 'red',
                autoClose: 6000,
              });
            }
          } else {
            notifications.show({
              title: 'Erro inesperado',
              message: 'Ocorreu um erro inesperado. Tente novamente mais tarde.',
              color: 'red',
              autoClose: 6000,
            });
            rollbar.error(`Error during "${name}" action:`, error as Error);
          }
        }
      });
    },
    [skipErrorNotification, successMessage, successTitle, errorMessage, name],
  );

  return [pending, startSafeTransition, error] as const;
}
